// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, CircularProgress, Fab, Tooltip } from '@mui/material';
/* eslint-disable @nx/enforce-module-boundaries -- documents page Loader; cycle with aggregation drawers using EntityDocumentsSection */
import { Loader } from '@shared/advisory/aggregation/components/loader';
import { RefreshControl } from '@shared/advisory/aggregation/components/refresh-control';
/* eslint-enable @nx/enforce-module-boundaries */
import { AppIcon, PageContainer, SectionContainer } from '@shared/ui-kit';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { DocumentsActiveFilters } from '../documents-active-filters/documents-active-filters';
import { DocumentsCardList } from '../documents-card-list/documents-card-list';
import { DocumentsDetailDrawer } from '../documents-detail-drawer/documents-detail-drawer';
import { DocumentsFiltersDrawer } from '../documents-filters-drawer/documents-filters-drawer';
import { DocumentsTable } from '../documents-table/documents-table';
import { DocumentsToolbar } from '../documents-toolbar/documents-toolbar';
import { UploadDocumentDrawer } from '../upload-document-drawer/upload-document-drawer';
import { useDocumentsPage } from './use-documents-page';

export const DocumentsPage = defineComponent({
  displayName: 'DocumentsPage',
  useSetup: useDocumentsPage,
  View: ({
    allDocumentsCount,
    filteredDocuments,
    mobileVisibleDocuments,
    hasMoreMobileDocuments,
    onLoadMoreMobileDocuments,
    status,
    search,
    scope,
    activeFilterCount,
    drawerFilters,
    filtersDrawerOpen,
    onSearchChange,
    onScopeChange,
    onOpenFilters,
    onCloseFilters,
    onApplyDrawerFilters,
    onClearAllFilters,
    onRemoveDrawerFilter,
    onUpload,
    onCloseUploadDrawer,
    uploadDrawerOpen,
    onRefresh,
    onDownloadDocument,
    onViewDetailDocument,
    onDeleteDocument,
    detailDrawerOpen,
    selectedDocument,
    onCloseDetailDrawer,
    hasData,
    isToolbarDisabled,
    t,
    embedded,
    hideUpload,
    hideScopeTabs,
  }) =>
    embedded ? (
      <div className="relative" data-testid="documents-page-embedded">
        <div className="flex min-h-0 flex-1 flex-col lg:contents">
          <div className="shrink-0 lg:contents">
            <DocumentsToolbar
              scope={scope}
              onScopeChange={onScopeChange}
              search={search}
              onSearchChange={onSearchChange}
              activeFilterCount={activeFilterCount}
              onOpenFilters={onOpenFilters}
              disabled={isToolbarDisabled}
              hideScopeTabs={hideScopeTabs}
            />
            <DocumentsActiveFilters
              filters={drawerFilters}
              onRemoveFilter={onRemoveDrawerFilter}
              onClearAll={onClearAllFilters}
            />
          </div>
          <DocumentsFiltersDrawer
            open={filtersDrawerOpen}
            onClose={onCloseFilters}
            scope={scope}
            appliedFilters={drawerFilters}
            onApply={onApplyDrawerFilters}
          />
          <DocumentsDetailDrawer
            open={detailDrawerOpen}
            onClose={onCloseDetailDrawer}
            document={selectedDocument}
          />
          {hideUpload ? null : (
            <UploadDocumentDrawer
              open={uploadDrawerOpen}
              onClose={onCloseUploadDrawer}
            />
          )}
          <div className="mt-4 flex min-h-0 flex-1 flex-col lg:mt-0 lg:block lg:flex-none">
            <div className="mb-4 flex justify-end lg:hidden">
              <RefreshControl
                onRefresh={onRefresh}
                disabled={status === 'pending'}
                loading={status === 'fetching'}
              />
            </div>
            <Loader
              status={status}
              shouldDisplayContent={hasData}
              shouldDisplayEmptyState={!hasData}
              onRefresh={onRefresh}
              emptyStateTitle={
                <Translation i18nKey="documents.empty-title" ns="components" />
              }
              emptyStateDescription={
                <Translation
                  i18nKey="documents.empty-description"
                  ns="components"
                />
              }
              errorTitle={
                <Translation i18nKey="documents.error-title" ns="components" />
              }
            >
              <div className="hidden lg:block">
                <SectionContainer
                  testId="documents-table"
                  className="mt-4"
                  headerClass="mb-4"
                  title={
                    <Translation i18nKey="documents.list-title" ns="components" />
                  }
                  actions={
                    <div className="flex items-center gap-x-4">
                      {status === 'fetching' ? (
                        <CircularProgress size={20} data-testid="loading-spinner" />
                      ) : (
                        <Tooltip
                          title={t('common.refresh', { ns: 'common' })}
                          placement="top"
                        >
                          <span className="flex items-center justify-center">
                            <IconButton
                              icon="RefreshTwoArrows"
                              variant="icon"
                              size="xl"
                              color={status === 'pending' ? 'disabled' : 'main'}
                              onClick={onRefresh}
                              data-testid="refresh-btn"
                              disabled={status === 'pending'}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  }
                >
                  <DocumentsTable
                    data={filteredDocuments}
                    onDownload={onDownloadDocument}
                    onViewDetail={onViewDetailDocument}
                    onDelete={onDeleteDocument}
                  />
                </SectionContainer>
              </div>
              <div
                className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-24 lg:hidden"
                data-testid="documents-card-list-scroll"
              >
                <DocumentsCardList
                  data={mobileVisibleDocuments}
                  onViewDetail={onViewDetailDocument}
                />
                {hasMoreMobileDocuments ? (
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={onLoadMoreMobileDocuments}
                    className="normal-case"
                    data-testid="documents-load-more"
                  >
                    <Translation i18nKey="documents.load-more" ns="components" />
                  </Button>
                ) : null}
              </div>
            </Loader>
          </div>
        </div>
        {hideUpload || uploadDrawerOpen ? null : (
          <Fab
            color="secondary"
            onClick={onUpload}
            disabled={isToolbarDisabled}
            aria-label={t('documents.upload', { ns: 'components' })}
            className="!fixed !bottom-7 !right-7 !z-10 lg:!hidden"
            data-testid="documents-upload-fab"
          >
            <AppIcon icon="Plus" color="light" size="base" />
          </Fab>
        )}
      </div>
    ) : (
      <PageContainer
        titlePage={
          <span className="flex flex-col" data-testid="documents-page-title">
            <Translation i18nKey="documents.title" ns="components" />
            <span className="text-lg font-normal text-primary-medium">
              <Translation
                i18nKey="documents.registered-count"
                ns="components"
                values={{ count: allDocumentsCount }}
              />
            </span>
          </span>
        }
        actionBtn={
          hideUpload ? undefined : (
            <div className="hidden shrink-0 lg:block">
              <Button
                color="secondary"
                variant="contained"
                onClick={onUpload}
                disabled={isToolbarDisabled}
                startIcon={<AppIcon icon="Plus" color="light" size="base" />}
                className="h-10 normal-case"
                data-testid="documents-upload-button"
              >
                <Translation i18nKey="documents.upload" ns="components" />
              </Button>
            </div>
          )
        }
      >
        <div className="flex min-h-0 flex-1 flex-col lg:contents">
          <div className="shrink-0 lg:contents">
            <DocumentsToolbar
              scope={scope}
              onScopeChange={onScopeChange}
              search={search}
              onSearchChange={onSearchChange}
              activeFilterCount={activeFilterCount}
              onOpenFilters={onOpenFilters}
              disabled={isToolbarDisabled}
              hideScopeTabs={hideScopeTabs}
            />
            <DocumentsActiveFilters
              filters={drawerFilters}
              onRemoveFilter={onRemoveDrawerFilter}
              onClearAll={onClearAllFilters}
            />
          </div>
          <DocumentsFiltersDrawer
            open={filtersDrawerOpen}
            onClose={onCloseFilters}
            scope={scope}
            appliedFilters={drawerFilters}
            onApply={onApplyDrawerFilters}
          />
          <DocumentsDetailDrawer
            open={detailDrawerOpen}
            onClose={onCloseDetailDrawer}
            document={selectedDocument}
          />
          {hideUpload ? null : (
            <UploadDocumentDrawer
              open={uploadDrawerOpen}
              onClose={onCloseUploadDrawer}
            />
          )}
          <div className="mt-4 flex min-h-0 flex-1 flex-col lg:mt-0 lg:block lg:flex-none">
            <div className="mb-4 flex justify-end lg:hidden">
              <RefreshControl
                onRefresh={onRefresh}
                disabled={status === 'pending'}
                loading={status === 'fetching'}
              />
            </div>
            <Loader
              status={status}
              shouldDisplayContent={hasData}
              shouldDisplayEmptyState={!hasData}
              onRefresh={onRefresh}
              emptyStateTitle={
                <Translation i18nKey="documents.empty-title" ns="components" />
              }
              emptyStateDescription={
                <Translation
                  i18nKey="documents.empty-description"
                  ns="components"
                />
              }
              errorTitle={
                <Translation i18nKey="documents.error-title" ns="components" />
              }
            >
              <div className="hidden lg:block">
                <SectionContainer
                  testId="documents-table"
                  className="mt-4"
                  headerClass="mb-4"
                  title={
                    <Translation i18nKey="documents.list-title" ns="components" />
                  }
                  actions={
                    <div className="flex items-center gap-x-4">
                      {status === 'fetching' ? (
                        <CircularProgress size={20} data-testid="loading-spinner" />
                      ) : (
                        <Tooltip
                          title={t('common.refresh', { ns: 'common' })}
                          placement="top"
                        >
                          <span className="flex items-center justify-center">
                            <IconButton
                              icon="RefreshTwoArrows"
                              variant="icon"
                              size="xl"
                              color={status === 'pending' ? 'disabled' : 'main'}
                              onClick={onRefresh}
                              data-testid="refresh-btn"
                              disabled={status === 'pending'}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  }
                >
                  <DocumentsTable
                    data={filteredDocuments}
                    onDownload={onDownloadDocument}
                    onViewDetail={onViewDetailDocument}
                    onDelete={onDeleteDocument}
                  />
                </SectionContainer>
              </div>
              <div
                className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-24 lg:hidden"
                data-testid="documents-card-list-scroll"
              >
                <DocumentsCardList
                  data={mobileVisibleDocuments}
                  onViewDetail={onViewDetailDocument}
                />
                {hasMoreMobileDocuments ? (
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={onLoadMoreMobileDocuments}
                    className="normal-case"
                    data-testid="documents-load-more"
                  >
                    <Translation i18nKey="documents.load-more" ns="components" />
                  </Button>
                ) : null}
              </div>
            </Loader>
          </div>
        </div>
        {hideUpload || uploadDrawerOpen ? null : (
          <Fab
            color="secondary"
            onClick={onUpload}
            disabled={isToolbarDisabled}
            aria-label={t('documents.upload', { ns: 'components' })}
            className="!fixed !bottom-7 !right-7 !z-10 lg:!hidden"
            data-testid="documents-upload-fab"
          >
            <AppIcon icon="Plus" color="light" size="base" />
          </Fab>
        )}
      </PageContainer>
    ),
});
